/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 *
 * @author Satyaki Bose
 * @file lateWorkDeadlineSelectStyles.js
 */

import { styled } from '@mui/material/styles';
import mixins from '../../../../assets/styles/mixins.scss';
import {
  getAssignmentDueDateCalendarHeaderStyles,
  getAssignmentDueDateCalendarStyles
} from '../../styles/assignmentDueDateCalendarStyles';
import getSettingsSelectShellStyles from './settingsSelectSharedStyles';
import {
  createGlobalStyles,
  createScopedClasses,
  createScopedStyles
} from '../../../../assets/styles/createScopedStyles';

const calendarHeaderStyles = getAssignmentDueDateCalendarHeaderStyles();
const settingsSelectShellStyles = getSettingsSelectShellStyles();

const lateWorkDeadlineSelectStyles = {
  '@global': {
    '#react-datepicker-portal': { zIndex: '9999 !important' },
    '.late-work-deadline-datepicker-popper': {
      zIndex: '9999 !important'
    }
  },
  wrapper: {
    width: '100%',
    minWidth: 0
  },
  ...settingsSelectShellStyles,
  menuPaper: {
    overflow: 'visible',
    maxHeight: 'none',
    boxShadow: mixins.paperElevation8BoxShadow,
    '--Paper-shadow': mixins.paperElevation8BoxShadow,
    ...settingsSelectShellStyles.menuPaper
  },
  customPanelItem: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    backgroundColor: mixins.quaternary,
    cursor: 'default',
    pointerEvents: 'auto'
  },
  customPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    padding: '8px 16px 16px',
    boxSizing: 'border-box',
    backgroundColor: mixins.quaternary
  },
  datePickerContainer: {
    position: 'relative',
    width: '100%',

    '& .react-datepicker__close-icon': {
      display: 'none'
    }
  },
  calendarPickerSlot: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'stretch',

    '& .react-datepicker-wrapper': {
      width: 'auto',
      display: 'flex',
      alignItems: 'stretch'
    },

    '& .react-datepicker__input-container': {
      width: 'auto',
      display: 'flex',
      alignItems: 'stretch'
    }
  },
  dateInputRow: {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    height: 48,
    boxSizing: 'border-box',
    borderRadius: 4,
    border: `1px solid ${mixins.disabledInputBoxColor}`,
    backgroundColor: mixins.chalkWhiteFont,
    overflow: 'hidden',
    '&:has(input:focus-visible)': {
      borderColor: mixins.amethystColorV2,
      '& $dateInputSeparator': {
        backgroundColor: mixins.amethystColorV2
      }
    }
  },
  dateInputRowInvalid: {
    borderColor: mixins.accentRedDark
  },
  dateInputField: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    boxSizing: 'border-box',
    border: 'none',
    padding: '12px',
    backgroundColor: 'transparent',
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.timeIconColor,
    outline: 'none',
    cursor: 'text',

    '&::placeholder': {
      color: mixins.greyScaleColor,
      opacity: 1
    },

    '&:disabled': {
      cursor: 'not-allowed',
      color: mixins.greyScaleColor
    }
  },
  dateInputSeparator: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: mixins.disabledInputBoxColor,
    flexShrink: 0
  },
  calendarIconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    flexShrink: 0,
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: mixins.amethyst,

    '& svg': {
      fontSize: 16,
      width: 16,
      height: 16
    },

    '&:hover:not(:disabled)': {
      backgroundColor: mixins.quaternary
    },

    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5
    }
  },
  datePickerPopper: {
    zIndex: '9999 !important'
  },
  datePickerPortalWrapper: {
    maxWidth: '100%',
    ...getAssignmentDueDateCalendarStyles({ withShadow: true, forAssignmentForm: true }),
    '& .react-datepicker': {
      width: 320,
      maxWidth: 'calc(100vw - 32px)'
    }
  },
  calendarHeader: calendarHeaderStyles.customHeader,
  calendarNavButton: calendarHeaderStyles.navButton,
  calendarMonthYearLabel: calendarHeaderStyles.monthYearLabel,
  applyButton: {
    alignSelf: 'center',
    minWidth: 120,
    height: 40,
    borderRadius: 8,
    padding: '8px 24px',
    border: 'none',
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight600,
    lineHeight: '24px',
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: mixins.amethyst
    },

    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  }
};

export const classes = createScopedClasses('aet-late-work-deadline-select', lateWorkDeadlineSelectStyles);

export const scopedStyles = createScopedStyles(lateWorkDeadlineSelectStyles, classes);

export const globalStyles = createGlobalStyles(lateWorkDeadlineSelectStyles, classes);

export const LateWorkDeadlineSelectStylesRoot = styled('div')(scopedStyles);

export default lateWorkDeadlineSelectStyles;
